import { useFocusRing } from '@react-aria/focus';
import { useTableColumnHeader } from '@react-aria/table';
import { mergeProps } from '@react-aria/utils';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { TableState } from '@react-stately/table';
import { GridNode } from '@react-types/grid';
import React, { useImperativeHandle, useRef } from 'react';

import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cx, VariantProps } from 'class-variance-authority';
import { columnHeaderClassNames } from './styles';

interface Props<T> {
    column: GridNode<T>;
    state: TableState<T>;
}

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableColumnHeaderProps<T = unknown> = Props<T> & VariantProps<typeof columnHeaderClassNames> & NativeAttrs;

const TableColumnHeader = React.forwardRef<HTMLTableRowElement, TableColumnHeaderProps>(
    ({ column, state, className, ...props }, ref: React.Ref<HTMLTableRowElement | null>) => {
        const tableColumnHeaderRef = useRef<HTMLTableRowElement | null>(null);

        useImperativeHandle(ref, () => tableColumnHeaderRef?.current);

        const {
            columnHeaderProps
        }: {
            columnHeaderProps: Omit<React.HTMLAttributes<unknown>, keyof TableColumnHeaderProps<unknown>>;
        } = useTableColumnHeader({ node: column }, state, tableColumnHeaderRef);

        const { isFocusVisible, focusProps } = useFocusRing();
        const { hideHeader, align, allowsSorting, width, ...columnProps } = column.props;

        return (
            <th
                ref={tableColumnHeaderRef}
                className={columnHeaderClassNames({
                    className,
                    isFocusVisible,
                    allowsSorting,
                    align,
                    isSorted: state.sortDescriptor?.column !== column.key
                })}
                colSpan={column.colspan}
                {...mergeProps(props, columnHeaderProps, focusProps, columnProps)}
                style={width === undefined ? props.style : { ...props.style, width }}
            >
                {hideHeader ? <VisuallyHidden>{column.rendered}</VisuallyHidden> : column.rendered}
                {!columnProps.allowsSorting && (
                    <FontAwesomeIcon
                        icon={state.sortDescriptor?.direction !== 'ascending' ? faChevronCircleDown : faChevronCircleUp}
                        size="lg"
                        className={cx(
                            state.sortDescriptor?.column !== column.key && 'tw-hidden',
                            'tw-absolute tw-top-1/2 tw-my-0 tw-ml-4 -tw-translate-y-1/2 tw-text-success'
                        )}
                    />
                )}
            </th>
        );
    }
);

TableColumnHeader.displayName = 'TableColumnHeader';

export default TableColumnHeader;
